import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useActivacions } from '@/api/activacions';
import { useMissatgeError } from '@/api/errors';
import {
  periodicitatDeCron,
  useCommutaInformeProgramat,
  useEliminaInformeProgramat,
  useIndicadorsArees,
  useInformesProgramats,
} from '@/api/informes';
import { useArees, useDepartaments } from '@/api/planificacio';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { BadgeEstat, BadgeTipus, Buit } from '@/pages/operacio/comu';
import { csv, descarregaCsv } from '@/pages/planificacio/calendari';
import { useMe } from '@/permisos/MeContext';
import { tePermis } from '@/permisos/permisos';
import { SelectorPeriode, Tendencia, XipSelect } from './comu';
import { tendencia, usePeriode, useTemps } from './suport';

export function W41Informes() {
  const { t } = useTranslation('informes');
  const f = useFormat();
  const temps = useTemps();
  const missatgeError = useMissatgeError();
  const { me } = useMe();
  const potCrear = tePermis(me, 'cai.informes', 'ALTA');
  const potAuditar = tePermis(me, 'cai.auditoria', 'CONSULTAR');
  const { periode, setPeriode, desde, fins, anteriorDesde } = usePeriode('MES');
  const [departamentId, setDepartamentId] = useState<number | ''>('');
  const departaments = useDepartaments();
  const arees = useArees();
  const llistaArees = (arees.data ?? []).filter(
    (a) => departamentId === '' || a.departamentId === departamentId,
  );
  const ids = llistaArees.map((a) => a.id);
  const actuals = useIndicadorsArees(ids, desde, fins);
  const anteriors = useIndicadorsArees(ids, anteriorDesde, desde);
  const programats = useInformesProgramats();
  const commuta = useCommutaInformeProgramat();
  const elimina = useEliminaInformeProgramat();
  const recents = useActivacions({ desde, fins, size: 10, page: 0 });
  const nomDep = (id: number) => departaments.data?.find((d) => d.id === id)?.nom ?? '';
  const pct = (v?: number | null) => (v == null ? '—' : f.percentatge(v));

  const files = llistaArees.map((a, i) => ({
    area: a,
    actual: actuals[i]?.data,
    anterior: anteriors[i]?.data,
  }));

  const exporta = () =>
    descarregaCsv('informe-global.csv', [
      csv([
        t('comu.departament'),
        t('comu.area'),
        t('w40.kpi.activacions'),
        t('w40.kpi.primeraConfirmacio'),
        t('w40.kpi.resolucio'),
        t('w40.kpi.confirmacio'),
        t('w41.tendencia'),
      ]),
      ...files.map((r) =>
        csv([
          nomDep(r.area.departamentId),
          r.area.nom,
          r.actual?.activacions ?? 0,
          temps(r.actual?.tempsMitjaPrimeraConfirmacioSeg),
          temps(r.actual?.tempsMitjaResolucioSeg),
          pct(r.actual?.percentatgeConfirmacio),
          tendencia(r.actual?.activacions ?? 0, r.anterior?.activacions ?? 0) ?? '',
        ]),
      ),
    ]);

  const errorProgramats = commuta.error ?? elimina.error;

  return (
    <section className="pantalla" data-screen="W-41">
      <Toolbar titol={t('titols.informes')}>
        <SelectorPeriode valor={periode} onCanvia={setPeriode} />
        {potCrear ? (
          <Link className="btn btn--s" to="/analisi/informes/nou?programa=1">
            <Icona nom="calendar-clock" />
            {t('w41.programa')}
          </Link>
        ) : null}
        <button type="button" className="btn btn--s" onClick={exporta} disabled={!files.length}>
          <Icona nom="doc-export" />
          {t('comu.exporta')}
        </button>
        {potCrear ? (
          <Link className="btn btn--primary btn--s" to="/analisi/informes/nou">
            <Icona nom="plus" />
            {t('w41.nou')}
          </Link>
        ) : null}
      </Toolbar>
      <div className="content stack">
        <div className="panel">
          <div className="panel__head">
            <h3>{t('w41.comparativa')}</h3>
            <div className="spacer" />
            <span className="t-caption-2 dim">
              {t('w41.vsAnterior', { desde: f.data(anteriorDesde), fins: f.data(desde) })}
            </span>
            <XipSelect
              icona="building"
              etiqueta={t('comu.departament')}
              valor={departamentId}
              onCanvia={(v) => setDepartamentId(v ? Number(v) : '')}
              opcions={[
                { valor: '', etiqueta: t('comu.totsDepartaments') },
                ...(departaments.data ?? []).map((d) => ({ valor: String(d.id), etiqueta: d.nom })),
              ]}
            />
          </div>
          {arees.isError ? (
            <ErrorCarrega onReintenta={() => void arees.refetch()} />
          ) : files.length === 0 ? (
            <Buit icona="chart-bar">
              {arees.isLoading ? t('comu.carregant') : t('comu.senseDades')}
            </Buit>
          ) : (
            <table className="taula" data-testid="comparativa">
              <thead>
                <tr>
                  <th>{t('w41.equip')}</th>
                  <th className="num">{t('w40.kpi.activacions')}</th>
                  <th className="num">{t('w41.primeraConfirmacio')}</th>
                  <th className="num">{t('w41.resolucio')}</th>
                  <th className="num">{t('w41.confirmacio')}</th>
                  <th className="num">{t('w41.tendencia')}</th>
                </tr>
              </thead>
              <tbody>
                {files.map((r) => (
                  <tr key={r.area.id} data-area={r.area.id}>
                    <td>
                      {nomDep(r.area.departamentId) ? (
                        <span className="dim">{nomDep(r.area.departamentId)} · </span>
                      ) : null}
                      <span>{r.area.nom}</span>
                    </td>
                    <td className="num">{r.actual ? f.numero(r.actual.activacions) : '—'}</td>
                    <td className="num">{temps(r.actual?.tempsMitjaPrimeraConfirmacioSeg)}</td>
                    <td className="num">{temps(r.actual?.tempsMitjaResolucioSeg)}</td>
                    <td className="num">{pct(r.actual?.percentatgeConfirmacio)}</td>
                    <td className="num">
                      <Tendencia
                        valor={tendencia(r.actual?.activacions ?? 0, r.anterior?.activacions ?? 0)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="list-foot">{t('w41.peu')}</div>
        </div>
        <div className="grid grid-2">
          <div className="panel">
            <div className="panel__head">
              <h3>{t('w41.activacions')}</h3>
            </div>
            {(recents.data?.items ?? []).length === 0 ? (
              <Buit icona="exclamationmark-triangle">{t('comu.senseDades')}</Buit>
            ) : (
              <table className="taula">
                <tbody>
                  {(recents.data?.items ?? []).map((a) => (
                    <tr key={a.id}>
                      <td>
                        <div className="hstack gap-8">
                          <BadgeTipus tipus={a.tipus} critica={a.critica} />
                          <span className="codi">{a.codi}</span>
                        </div>
                        <div className="mt-4">{a.titol}</div>
                      </td>
                      <td>
                        <BadgeEstat estat={a.estat} />
                        <div className="t-caption-2 dim mt-4">{f.dataHora(a.createdAt)}</div>
                      </td>
                      <td>
                        <div className="hstack gap-4" style={{ justifyContent: 'flex-end' }}>
                          <Link
                            className="btn btn--ghost btn--xs"
                            to={`/analisi/informes/activacio/${a.id}`}
                          >
                            <Icona nom="doc-text" />
                            {t('w41.informe')}
                          </Link>
                          {potAuditar ? (
                            <Link
                              className="btn btn--ghost btn--xs"
                              to={`/analisi/tracabilitat/${a.id}`}
                            >
                              <Icona nom="link" />
                              {t('nav.tracabilitat')}
                            </Link>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="panel" data-testid="programats">
            <div className="panel__head">
              <h3>{t('w41.programats')}</h3>
            </div>
            {errorProgramats ? (
              <div className="avis-banda" role="alert" style={{ margin: '12px 16px 0' }}>
                <Icona nom="exclamationmark-triangle" />
                {missatgeError(errorProgramats)}
              </div>
            ) : null}
            {programats.isError ? (
              <ErrorCarrega onReintenta={() => void programats.refetch()} />
            ) : (programats.data ?? []).length === 0 ? (
              <Buit icona="calendar-clock">{t('w41.senseProgramats')}</Buit>
            ) : (
              <div>
                {(programats.data ?? []).map((p) => {
                  const per = periodicitatDeCron(p.cron);
                  return (
                    <div className="list-row" key={p.id} data-programat={p.id}>
                      <div className={`list-row__icon ${p.actiu ? 'bg-blue' : 'bg-gray'}`}>
                        <Icona nom="calendar-clock" />
                      </div>
                      <div className="list-row__text">
                        <div className="list-row__label">{p.nom}</div>
                        <div className="list-row__sub">
                          {t(`tipusProgramat.${p.tipus}`)} · {p.format} ·{' '}
                          {per ? t(`periodicitats.${per}`) : p.cron}
                          {p.ultimaExecucio
                            ? ` · ${t('w41.ultimaExecucio', { data: f.dataHora(p.ultimaExecucio) })}`
                            : ''}
                        </div>
                      </div>
                      {potCrear && p.id != null ? (
                        <>
                          <input
                            type="checkbox"
                            className="switch"
                            aria-label={t('w41.actiu')}
                            title={p.actiu ? t('w41.actiu') : t('w41.inactiu')}
                            checked={p.actiu}
                            disabled={commuta.isPending}
                            onChange={(e) =>
                              commuta.mutate({ id: p.id as number, actiu: e.target.checked })
                            }
                          />
                          <Link
                            className="btn btn--ghost btn--xs btn--icona"
                            to={`/analisi/informes/nou?id=${p.id}`}
                            aria-label={t('comu.edita')}
                            title={t('comu.edita')}
                          >
                            <Icona nom="pencil" />
                          </Link>
                          <button
                            type="button"
                            className="btn btn--ghost btn--xs btn--icona c-red"
                            aria-label={t('comu.elimina')}
                            title={t('comu.elimina')}
                            onClick={() => elimina.mutate(p.id as number)}
                          >
                            <Icona nom="trash" />
                          </button>
                        </>
                      ) : (
                        <span className={`badge badge--${p.actiu ? 'ok' : 'gray'}`}>
                          {p.actiu ? t('w41.actiu') : t('w41.inactiu')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
